(() => {
  const scriptPromises = new Map();
  const stylePromises = new Map();

  const absoluteUrl = (url) => new URL(url, document.baseURI).href;

  const existingScript = (url) =>
    Array.from(document.querySelectorAll("script[src]")).find(
      (script) => script.src === absoluteUrl(url),
    );

  const existingStyle = (url) =>
    Array.from(document.querySelectorAll('link[rel="stylesheet"][href]')).find(
      (link) => link.href === absoluteUrl(url),
    );

  const loadScript = (url, options = {}) => {
    if (!url) return Promise.resolve();

    const type = options.type === "module" ? "module" : "classic";
    const key = `${type}:${absoluteUrl(url)}`;
    if (scriptPromises.has(key)) return scriptPromises.get(key);
    if (existingScript(url)) {
      const resolved = Promise.resolve();
      scriptPromises.set(key, resolved);
      return resolved;
    }

    const promise = new Promise((resolve, reject) => {
      const script = document.createElement("script");
      if (type === "module") {
        script.type = "module";
      } else {
        script.async = false;
      }
      script.src = url;
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener(
        "error",
        () => reject(new Error(`Unable to load theme script: ${url}`)),
        { once: true },
      );
      document.head.appendChild(script);
    });

    scriptPromises.set(key, promise);
    return promise;
  };

  // Fetch in parallel; classic scripts still execute in list order because
  // loadScript sets async = false on each injected tag.
  const loadScripts = (urls, options = {}) =>
    Promise.all(urls.map((url) => loadScript(url, options)));

  const loadStyle = (url) => {
    if (!url) return Promise.resolve();

    const key = absoluteUrl(url);
    if (stylePromises.has(key)) return stylePromises.get(key);
    if (existingStyle(url)) {
      const resolved = Promise.resolve();
      stylePromises.set(key, resolved);
      return resolved;
    }

    const promise = new Promise((resolve, reject) => {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      link.addEventListener("load", resolve, { once: true });
      link.addEventListener(
        "error",
        () => reject(new Error(`Unable to load theme stylesheet: ${url}`)),
        { once: true },
      );
      document.head.appendChild(link);
    });

    stylePromises.set(key, promise);
    return promise;
  };

  const loadStyles = (urls) => Promise.all(urls.map(loadStyle));

  window.SIYOUThemeAssets = {
    loadScript,
    loadScripts,
    loadStyle,
    loadStyles,
  };

  if (!customElements.get("theme-asset-request")) {
    customElements.define(
      "theme-asset-request",
      class ThemeAssetRequest extends HTMLElement {
        connectedCallback() {
          if (this.requested) return;
          this.requested = true;

          const scripts = (this.dataset.scripts || "")
            .split(/\s+/)
            .filter(Boolean);
          const moduleScripts = (this.dataset.moduleScripts || "")
            .split(/\s+/)
            .filter(Boolean);
          const styles = (this.dataset.styles || "").split(/\s+/).filter(Boolean);

          Promise.all([
            loadStyles(styles),
            loadScripts(scripts).then(() =>
              loadScripts(moduleScripts, { type: "module" }),
            ),
          ]).catch((error) => console.warn(error));
        }
      },
    );
  }
})();
